/**
 * facility-rules.ts — WEB.4.UI.11a
 *
 * Lookup helpers for the per-(era, terrain, facilityType) build matrix
 * authored in `data/facility-rules.json` (source: D:\claude\GDG\tile list.docx).
 *
 * The existing `facilities.ts` (6-row primary list) is preserved as a
 * legacy artifact — current BuildMenu still uses it. UI.11b will swap
 * BuildMenu to consume this dynamic per-(era, terrain) lookup instead.
 */

import facilityRulesJson from "../../../../data/facility-rules.json";

export type FacilityType =
  | "Coal"
  | "Oil"
  | "Hydro"
  | "Biomass"
  | "Wind"
  | "Solar"
  | "Nuclear"
  | "Geothermal"
  | "WasteToEnergy"
  | "OffshoreDrilling"
  | "FloatingSolar"
  | "Tidal";

export interface FacilityRule {
  era: 1 | 2 | 3 | 4;
  terrain: string;
  facilityType: FacilityType;
  /** Display label per the source docx (e.g. "Hydropower dam" vs "Hydropower plant"). */
  label: string;
  cost: number;
  toll: number;
  pollution: number;
  energy: number;
}

const RULES: ReadonlyArray<FacilityRule> = facilityRulesJson.rules as ReadonlyArray<FacilityRule>;

/**
 * All facilities buildable on the given (era, terrain) pair.
 *
 * Returns an empty array when the terrain is unbuildable in that era
 * (e.g. era 1 Desert / Coast / Tundra are off-limits).
 */
export function getAvailableFacilities(
  era: 1 | 2 | 3 | 4,
  terrain: string,
): ReadonlyArray<FacilityRule> {
  return RULES.filter((r) => r.era === era && r.terrain === terrain);
}

/** Single (era, terrain, facilityType) lookup. Returns null when not allowed. */
export function getFacilityRule(
  era: 1 | 2 | 3 | 4,
  terrain: string,
  facilityType: FacilityType,
): FacilityRule | null {
  return (
    RULES.find((r) => r.era === era && r.terrain === terrain && r.facilityType === facilityType) ??
    null
  );
}

/** All distinct FacilityTypes unlocked by the given era (cumulative). */
export function getUnlockedFacilityTypes(era: 1 | 2 | 3 | 4): ReadonlyArray<FacilityType> {
  const found = new Set<FacilityType>();
  for (const r of RULES) {
    if (r.era <= era) found.add(r.facilityType);
  }
  return Array.from(found);
}

/** All terrain types that allow at least one build in the given era. */
export function getBuildableTerrainsForEra(era: 1 | 2 | 3 | 4): ReadonlyArray<string> {
  const found = new Set<string>();
  for (const r of RULES) {
    if (r.era === era) found.add(r.terrain);
  }
  return Array.from(found);
}

/**
 * R3 Bug 3 — All facilities ever buildable on a terrain (across all eras),
 * deduplicated by facilityType, each carrying the `minEra` at which it first
 * unlocks. Sorted ascending by minEra so the list reads era-1→4.
 *
 * Used by BuildMenu to render era-locked rows (visible but disabled) instead
 * of hiding them entirely. getAvailableFacilities() is NOT changed.
 */
export function getAllFacilitiesForTerrain(
  terrain: string,
): ReadonlyArray<FacilityRule & { minEra: 1 | 2 | 3 | 4 }> {
  // Codex precommit LOW: preserve authored-rule order within each minEra group
  // so the current-era subset of the result matches getAvailableFacilities() order.
  // Track the source index of the first rule that introduced each facilityType.
  const byType = new Map<FacilityType, FacilityRule & { minEra: 1 | 2 | 3 | 4; _idx: number }>();
  RULES.forEach((r, idx) => {
    if (r.terrain !== terrain) return;
    const existing = byType.get(r.facilityType);
    if (!existing || r.era < existing.minEra) {
      byType.set(r.facilityType, { ...r, minEra: r.era, _idx: idx });
    }
  });
  // Stable sort: primary = minEra asc, secondary = source index asc.
  const out = Array.from(byType.values()).sort((a, b) =>
    a.minEra !== b.minEra ? a.minEra - b.minEra : a._idx - b._idx,
  );
  // Strip the internal _idx field from the returned shape.
  return out.map(({ _idx, ...rest }) => {
    void _idx;
    return rest;
  });
}
